import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, LinearProgress } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styles from './CallDrawerModern.module.scss'

const STEP_LABELS = {
  transcript: 'Transcript',
  summary: 'Summary',
  sentiment: 'Sentiment',
  qa: 'QA Evaluation'
}

const STEP_ICONS = {
  transcript: 'file-alt',
  summary: 'brain',
  sentiment: 'chart-line',
  qa: 'clipboard-check'
}

export const AIProgressBar = ({ steps, isGenerating, isComplete, error, onRetry }) => {
  const { progress, statusText } = useMemo(() => {
    const total = steps.length
    const completed = steps.filter(s => s.status === 'complete' || s.status === 'skipped').length
    const inProgress = steps.find(s => s.status === 'in-progress')
    const hasError = steps.some(s => s.status === 'error')

    let statusText = ''
    if (hasError) {
      const errorStep = steps.find(s => s.status === 'error')
      statusText = `Error: ${errorStep?.error || 'Generation failed'}`
    } else if (isComplete) {
      statusText = 'AI Analysis Complete'
    } else if (inProgress) {
      statusText = `Generating ${STEP_LABELS[inProgress.id]}...`
    } else {
      statusText = 'Preparing AI Analysis...'
    }

    return {
      progress: (completed / total) * 100,
      statusText
    }
  }, [steps, isComplete])

  if (!isGenerating && !isComplete && !error) return null

  const hasError = steps.some(s => s.status === 'error')

  return (
    <Box className={`${styles.aiProgressBar} ${isComplete && !hasError ? styles.aiProgressComplete : ''} ${hasError ? styles.aiProgressError : ''}`}>
      <Box className={styles.aiProgressHeader}>
        <Box className={styles.aiProgressTitle}>
          <FontAwesomeIcon
            icon={hasError ? 'exclamation-circle' : isComplete ? 'check-circle' : 'robot'}
            className={styles.aiProgressIcon}
          />
          <Typography className={styles.aiProgressText}>
            {statusText}
          </Typography>
        </Box>
        {hasError && onRetry && (
          <button className={styles.aiProgressRetryButton} onClick={onRetry}>
            <FontAwesomeIcon icon="redo" />
            Retry
          </button>
        )}
      </Box>

      <LinearProgress
        variant="determinate"
        value={progress}
        className={styles.aiProgressLinear}
        classes={{
          bar: hasError ? styles.aiProgressBarError : styles.aiProgressBarFill
        }}
      />

      <Box className={styles.aiProgressSteps}>
        {steps.map((step) => {
          const isActive = step.status === 'in-progress'
          const isDone = step.status === 'complete'
          const isSkipped = step.status === 'skipped'
          const isError = step.status === 'error'

          return (
            <Box
              key={step.id}
              className={`${styles.aiProgressStep} ${isActive ? styles.aiProgressStepActive : ''} ${isDone ? styles.aiProgressStepDone : ''} ${isSkipped ? styles.aiProgressStepSkipped : ''} ${isError ? styles.aiProgressStepError : ''}`}
            >
              <FontAwesomeIcon
                icon={isError ? 'times' : isDone ? 'check' : isSkipped ? 'minus' : STEP_ICONS[step.id]}
                className={styles.aiProgressStepIcon}
              />
              <span className={styles.aiProgressStepLabel}>{STEP_LABELS[step.id]}</span>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

AIProgressBar.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['pending', 'in-progress', 'complete', 'skipped', 'error']).isRequired,
    error: PropTypes.string
  })).isRequired,
  isGenerating: PropTypes.bool,
  isComplete: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func
}

export default AIProgressBar
